class FeatureCarousel extends HTMLElement {
  constructor(){
    super();

    this.slides = this.querySelectorAll(".slide");
    this.previews = this.querySelectorAll(".preview-button");
    this.previewTimers = this.querySelectorAll(".preview-timer");
    this.counters = this.querySelectorAll(".slider-counter__button");
    this.counterTimers = this.querySelectorAll(".dot-timer");
    this.nextBtn = this.querySelector("#nextSlide");
    this.prevBtn = this.querySelector("#prevSlide");

    this.slideControlGroups = [this.previews, this.counters];
    this.slidesPlusControls = [this.slides, this.previews, this.counters];
    this.timeIndicators = [...this.previewTimers, ...this.counterTimers];

    this.totalSlides = this.slides.length;
    this.currentIndex = 0;
    this.startX = 0;
    this.startY = 0;
    this.endX = 0;
    this.endY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.autoPlayInterval;
    this.slideDuration = this.dataset.seconds * 1000;
    this.themeEditor = window.Shopify && Shopify.designMode ? true : false;
    this.autoplayEnabled = this.dataset.autoplay === "true" && !this.themeEditor; 
    
    this.swipeDirection = null;
    this.isDragging = false;
    this.isHorizontalSwipe = null;
    this.dragDistance = 0;
    this.slideWidth = 0;
    this.throttleDelay = 10;
    this.lastDragUpdateTime = 0;

    this.onlyTwoSlides = this.totalSlides === 2;
    this.transitioning = false;
    this.slideTransitioned = false;

    this.transitionDuration = 300; //in milliseconds
    this.translateXright = 'translateX(100%)';
    this.translateXleft = 'translateX(-100%)';
    this.translateXcenter = 'translate(0%)';
    this.transitionStyle = `transform ${this.transitionDuration}ms ease-out`;
    this.transitionNone = 'none';
  }

  connectedCallback() {
    this.bindEvents();
    this.setSlidePositions();
    this.setAttributes();
    this.updateSlideWidth();
    if (this.autoplayEnabled) this.startAutoPlay();
    this.initializeFirstSlideAnimation();

    window.addEventListener("resize", () => this.updateSlideWidth());
  }

  bindEvents() {
    if (this.nextBtn) this.nextBtn.addEventListener("click", () => this.handleNextClick());
    if (this.prevBtn) this.prevBtn.addEventListener("click", () => this.handlePrevClick());

    this.addEventListener("touchstart", (event) => this.handleTouchStart(event), { passive: false });
    this.addEventListener("touchmove", (event) => this.handleTouchMove(event), { passive: false });
    this.addEventListener("touchend", (event) => this.handleTouchEnd(event));

    this.addEventListener("mousedown", (event) => this.handleMouseDown(event));
    this.addEventListener("mousemove", (event) => this.handleMouseMove(event));
    this.addEventListener("mouseup", (event) => this.handleMouseUp(event));
    this.addEventListener("mouseleave", (event) => this.handleMouseUp(event));

    this.slideControlGroups.forEach((control) => {
      control.forEach((btn, index) => {
        btn.addEventListener("click", (event) => this.handleControlClick(index, event));
      });
    });
  } 

  updateSlideWidth() {
    if (this.slides.length > 0) {
      this.slideWidth = this.slides[0].offsetWidth;
    }
  }

  initializeFirstSlideAnimation() {
    const firstImage = this.slides[0].querySelector('.slide-media__image');
    if (firstImage) requestAnimationFrame(() => firstImage.classList.add("initial")); 
  }

  startAutoPlay() {
    this.stopAutoPlay();
    this.resetAnimations();
    this.autoPlayInterval = setInterval(() => this.nextSlide(), this.slideDuration);
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearTimeout(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  disableTransitionExcept(activeIndices) {
    this.slides.forEach((slide, index) => {
      slide.style.transition = !activeIndices.includes(index) ? this.transitionStyle : this.transitionNone;
    })
  }

  enableSmoothTransition(slides) {
    requestAnimationFrame(() => {
      slides.forEach(slide => {
        slide.style.transition = this.transitionStyle;
      })
    })
  }

  nextSlide() {
    if (this.transitioning) return;
    this.transitioning = true;

    const currentSlide = this.slides[this.currentIndex];

    if (this.onlyTwoSlides) {
      const nextIndex = this.currentIndex === 0 ? 1 : 0;
      const nextSlide = this.slides[nextIndex];
      const nextSlidePosition = nextSlide.style.transform;
      
      if(this.slideTransitioned) {
        nextSlide.style.transform = nextSlidePosition;
      } else {
        nextSlide.style.transition = this.transitionNone;
        nextSlide.style.transform = this.translateXright; 
        nextSlide.offsetHeight;
      }

      currentSlide.style.transition = this.transitionStyle;      
      currentSlide.style.transform = this.translateXleft;

      nextSlide.style.transition = this.transitionStyle;
      nextSlide.style.transform = this.translateXcenter;

      this.currentIndex = nextIndex;
    } else {
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;
      const nextSlide = this.slides[nextIndex];

      this.disableTransitionExcept([this.currentIndex, nextIndex]);
      this.enableSmoothTransition([currentSlide, nextSlide]);

      currentSlide.style.transform = this.translateXleft;
      currentSlide.style.transition = this.transitionStyle;
      
      nextSlide.style.transform = this.translateXcenter;
      nextSlide.style.transition = this.transitionStyle;

      this.currentIndex = nextIndex;
    }

    this.swipeDirection = "right";
    this.updateSlides(this.currentIndex);

    setTimeout(() => {
      this.transitioning = false;
    }, this.transitionDuration);
  }

  prevSlide() {
    if (this.transitioning) return;
    this.transitioning = true;

    const currentSlide = this.slides[this.currentIndex];

    if (this.onlyTwoSlides) {
      const prevIndex = this.currentIndex === 0 ? 1 : 0;
      const prevSlide = this.slides[prevIndex];
      const prevSlidePosition = prevSlide.style.transform;

      if(this.slideTransitioned) {
        prevSlide.style.transform = prevSlidePosition;
      } else {
        prevSlide.style.transition = this.transitionNone;
        prevSlide.style.transform = this.translateXleft; 
        prevSlide.offsetHeight;
      }
      
      currentSlide.style.transition = this.transitionStyle;
      currentSlide.style.transform = this.translateXright;

      prevSlide.style.transition = this.transitionStyle;
      prevSlide.style.transform = this.translateXcenter;
      
      this.currentIndex = prevIndex;
    } else {
      const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      const prevSlide = this.slides[prevIndex];

      this.disableTransitionExcept([this.currentIndex, prevIndex])
      this.enableSmoothTransition([currentSlide, prevSlide]);

      currentSlide.style.transform = this.translateXright;
      currentSlide.style.transition = this.transitionStyle;

      prevSlide.style.transform = this.translateXcenter;
      prevSlide.style.transition = this.transitionStyle;

      this.currentIndex = prevIndex;
      this.swipeDirection = "left";
      this.updateSlides(this.currentIndex);

      this.currentIndex = prevIndex;
    }

    this.swipeDirection = "left";
    this.updateSlides(this.currentIndex);

    setTimeout(() => {
      this.transitioning = false;
    }, this.transitionDuration);
  }

  goToSlideAndPause(index) {
    if (index === this.currentIndex || this.transitioning) return;

    this.autoplayEnabled = false;
    
    if (this.onlyTwoSlides) {
      this.nextSlide();
    } else {
      this.getSwipeDirection(index);
      if (this.swipeDirection === "left") {
        this.prevSlide();
      } else if (this.swipeDirection === "right") {
        this.nextSlide();
      } else {
        this.nextSlide();
      }
    }
    
    this.stopAnimations();
  }

  updateSlides(slideIndex) {
    this.slides.forEach(slide => slide.classList.remove("initial"));
    
    this.stopAutoPlay();
    this.setSlidePositions();
    this.updateActiveStates(slideIndex);
    this.setAttributes();
    this.currentIndex = slideIndex;

    if (this.autoplayEnabled) this.startAutoPlay();
  }

  setSlidePositions() {
    if (this.slides.length === 1) return;

    const currentSlide = this.slides[this.currentIndex];

    if(this.onlyTwoSlides) {
      const otherIndex = this.currentIndex === 0 ? 1 : 0;
      const otherSlide = this.slides[otherIndex];
      
      requestAnimationFrame(() => {
        setTimeout(() => {
          currentSlide.style.transform = this.translateXcenter;
          currentSlide.style.transition = this.transitionNone;

          otherSlide.style.transform = this.swipeDirection === "left" ? this.translateXleft : this.translateXright;
          otherSlide.style.transition = this.transitionNone;
        }, this.transitionDuration);
      });
    } else {
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;
      const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      const nextSlide = this.slides[nextIndex];
      const prevSlide = this.slides[prevIndex];

      requestAnimationFrame(() => {
        setTimeout(() => {
          currentSlide.style.transform = this.translateXcenter;
          nextSlide.style.transform = this.translateXright;
          prevSlide.style.transform = this.translateXleft;
          nextSlide.style.transition = this.transitionNone;
          currentSlide.style.transition = this.transitionNone;
          prevSlide.style.transition = this.transitionNone;
        }, this.transitionDuration);
      });
    }
    
  }

  disableTransitions() {
    this.slides.forEach(slide => slide.style.transition = this.transitionNone);
    this.slides[0].offsetHeight;
  }

  disableButtonTemporarily(button) {
    button.disabled = true;
    setTimeout(() => {
      button.disabled = false;
    }, this.transitionDuration);
  }

  handleNextClick() {
    if (this.nextBtn) {
      this.disableButtonTemporarily(this.nextBtn);
      this.autoplayEnabled = true;
      this.nextSlide();
    }
  }

  handlePrevClick() {
    if (this.prevBtn) {
      this.disableButtonTemporarily(this.prevBtn);
      this.autoplayEnabled = true;
      this.prevSlide();
    }
  }

  handleControlClick(index, event) {
    if(this.transitioning) return;
    this.goToSlideAndPause(index);
    event.stopPropagation();
  }

  handleTouchStart(event) {
    if (this.transitioning) return;

    if (this.autoplayEnabled) this.stopAutoPlay();
    this.pauseAnimations();
    this.autoplayEnabled = false;

    this.startX = event.touches[0].clientX;
    this.startY = event.touches[0].clientY;
    this.currentX = this.startX;
    this.currentY = this.startY;
    this.isDragging = true;
    this.isHorizontalSwipe = null;
    this.swipeDirection = null;
    this.disableTransitions();

    event.stopPropagation();
  }

  handleTouchMove(event) {
    if (!this.isDragging || this.transitioning) return;

    this.currentX = event.touches[0].clientX;
    this.currentY = event.touches[0].clientY;

    const diffX = Math.abs(this.currentX - this.startX);
    const diffY = Math.abs(this.currentY - this.startY);
    

    if (this.isHorizontalSwipe === null) {
      if (diffX > 10 || diffY > 10) {
        this.isHorizontalSwipe = diffX >= diffY;
      }
    }

    if (this.isHorizontalSwipe === true) {
      event.preventDefault();

      const now = Date.now();
      if (now - this.lastDragUpdateTime >= this.throttleDelay) {
        this.lastDragUpdateTime = now;
        requestAnimationFrame(() => this.updateDragEffects());
      }
    }
  }

  handleTouchEnd(event) {
    if (!this.isDragging || this.transitioning) return;

    this.endX = event.changedTouches ? event.changedTouches[0].clientX : this.currentX;
    this.endY = event.changedTouches ? event.changedTouches[0].clientY : this.currentY;
    this.isDragging = false;

    this.slides.forEach(slide => {
      slide.style.transition = this.transitionStyle;
    });

    if (this.isHorizontalSwipe === true) {
      this.handleSwipe();
    } else {
      this.resetDragStyles();
    }

    this.isHorizontalSwipe = null;
  }

  updateDragEffects() {
    if (!this.isDragging) return;

    this.dragDistance = this.currentX - this.startX;
    this.swipeDirection = this.dragDistance < 0 ? "right" : "left";

    let dragPercent = (this.dragDistance / this.slideWidth) * 100;

    const currentSlide = this.slides[this.currentIndex];

    if (this.onlyTwoSlides) {
      const otherIndex = this.currentIndex === 0 ? 1 : 0;
      const otherSlide = this.slides[otherIndex];
      
      currentSlide.style.transition = this.transitionNone;
      otherSlide.style.transition = this.transitionNone;
      
      currentSlide.style.transform = `translate3d(${dragPercent}%, 0, 0)`;
      
      if (this.dragDistance < 0) {
        otherSlide.style.transform = `translate3d(${100 + dragPercent}%, 0, 0)`;
      } else {
        otherSlide.style.transform = `translate3d(${-100 + dragPercent}%, 0, 0)`;
      }

      if (Math.abs(this.dragDistance) > 10) { 
        this.slideTransitioned = true;
      }
    } else {
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;
      const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      const nextSlide = this.slides[nextIndex];
      const prevSlide = this.slides[prevIndex];

      currentSlide.style.transition = this.transitionNone;
      nextSlide.style.transition = this.transitionNone;
      prevSlide.style.transition = this.transitionNone;

      currentSlide.style.transform = `translate3d(${dragPercent}%, 0, 0)`;
      
      if (this.dragDistance < 0) {
        nextSlide.style.transform = `translate3d(${100 + dragPercent}%, 0, 0)`;
      } else {
        prevSlide.style.transform = `translate3d(${-100 + dragPercent}%, 0, 0)`;
      }
    }
    
  }

  resetDragStyles() {
    this.slides.forEach(slide => {
      slide.style.transition = this.transitionStyle;
    });

    const currentSlide = this.slides[this.currentIndex];

    if (this.onlyTwoSlides) {
      const otherIndex = this.currentIndex === 0 ? 1 : 0;
      const otherSlide = this.slides[otherIndex];
      
      currentSlide.style.transform = this.translateXcenter;

      otherSlide.style.transform = this.swipeDirection === "right" ? this.translateXright : this.translateXleft;
    } else {
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;
      const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      const nextSlide = this.slides[nextIndex];
      const prevSlide = this.slides[prevIndex];

      currentSlide.style.transform = this.translateXcenter;
      nextSlide.style.transform = this.translateXright;
      prevSlide.style.transform = this.translateXleft;
    }
  }

  handleMouseDown(event) {
    if (this.transitioning) return;

    if (this.autoplayEnabled) this.stopAutoPlay();

    this.pauseAnimations();
    this.autoplayEnabled = false;

    this.startX = event.clientX;
    this.currentX = this.startX;
    this.isDragging = true;
    this.swipeDirection = null;

    this.disableTransitions();
    event.preventDefault();
  }

  handleMouseMove(event) {
    if (!this.isDragging || this.transitioning) return;

    this.currentX = event.clientX;

    const now = Date.now();
    if (now - this.lastDragUpdateTime >= this.throttleDelay) {
      this.lastDragUpdateTime = now;
      requestAnimationFrame(() => this.updateDragEffects());
    }
  }

  handleMouseUp(event) {
    if (!this.isDragging || this.transitioning) return;

    this.endX = this.currentX;
    this.isDragging = false;

    this.slides.forEach(slide => {
      slide.style.transition = this.transitionStyle;
    });

    this.handleSwipe();
  }

  handleSwipe() {
    if (this.transitioning) return;
    
    const swipeDiff = this.startX - this.endX;
    const transitionThreshold = this.slideWidth / 3;

    if (Math.abs(swipeDiff) < 10) {
      this.resetDragStyles();
      return;
    }

    if (swipeDiff > transitionThreshold) {
      this.nextSlide();
    } else if(swipeDiff < -transitionThreshold) {
      this.prevSlide();
    } else {
      this.resetDragStyles();
    }
    
    this.stopAnimations();
    this.startX = 0;
    this.endX = 0;
    this.slideTransitioned = false;
  }

  getSwipeDirection(index) {
    if (this.onlyTwoSlides) {
      this.swipeDirection = this.currentIndex !== index ? (this.currentIndex === 0 ? "right" : "left") : null;
    } else {
      if (this.currentIndex === this.totalSlides - 1 && index === 0) {
        this.swipeDirection = "right";
      } else if (this.currentIndex === 0 && index === this.totalSlides - 1) {
        this.swipeDirection = "left";
      } else {
        this.swipeDirection = index > this.currentIndex ? "right" : "left";
      }
    }
  }

  updateActiveStates(slideIndex) {
    this.slidesPlusControls.forEach((element) => {
      element.forEach((elem, index) => {
        elem.classList.toggle("active", index === slideIndex);
      });
    });
  }

  resetAnimations() {
    this.timeIndicators.forEach((timer) => {
      timer.style.animation = "none";
      timer.offsetHeight;
      timer.style.animation = `preview-timer ${this.slideDuration / 1000}s linear forwards running`
    });
  }

  stopAnimations() {
    this.timeIndicators.forEach((timer) => timer.style.animation = "none");
    this.classList.add('paused');
  }

  pauseAnimations() {
    this.timeIndicators.forEach((timer) => timer.style.animationPlayState = "paused");
  }

  editSlide(slideIndex) {
    this.goToSlideAndPause(slideIndex);
    this.slides.forEach((slide, index) => {
      slide.classList.toggle("initial", index === slideIndex);
    });
  }

  setAttributes() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute("aria-hidden", index === this.currentIndex ? "false" : "true");
      slide.setAttribute("aria-label", index === this.currentIndex ? "Current Slide" : `${index + 1} of ${this.slides.length}`);
    });
    
    this.counters.forEach((counter, index) => {
      counter.setAttribute("aria-label", `${index + 1} of ${this.counters.length}`);
      counter.setAttribute("aria-disabled", index === this.currentIndex ? "true" : "false");
    });
  }
}

customElements.define('feature-carousel', FeatureCarousel);

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:block:select', function (event) {
    const blockSelectedIsSlide = event.target.classList.contains('slide');
    if (!blockSelectedIsSlide) return;

    const blockId = event.detail.blockId;
    const slideElem = document.querySelector(`[data-block-id="${blockId}"]`);
    const slideIndex = parseInt(slideElem.getAttribute('data-block-index'), 10);

    const parentFeatureCarousel = event.target.closest('feature-carousel');
    parentFeatureCarousel.editSlide(slideIndex);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    const blockSelectedIsSlide = event.target.classList.contains('slide');
    if (!blockSelectedIsSlide) return;
  });
}
